import { useQuery } from '@apollo/client/react';
import { GetWorkspaceCreditSummaryDocument } from '@graphql/queries/credit-summary/core.graphql';
import { useCallback, useMemo } from 'react';

import { type CreditSummaryView, deriveCreditSummaryView } from './derive-credit-summary-view';

interface UseCreditSummaryOptions {
  /**
   * Hold the query back while the consumer is not showing anything derived from
   * it. The summary is derived on read in the ledger (a full-month `deriveUsage`
   * per call), so an always-mounted consumer must skip until it is actually
   * visible rather than firing on every page load.
   */
  skip?: boolean;
  /**
   * Revalidate against the network on every mount/unskip instead of trusting
   * the cache, for a consumer that must not show a stale pending request after
   * ops resolve it.
   */
  revalidate?: boolean;
}

export function useCreditSummary(
  workspaceId: string,
  options?: UseCreditSummaryOptions,
): CreditSummaryView {
  const { data, loading, error, refetch } = useQuery(GetWorkspaceCreditSummaryDocument, {
    variables: { workspaceId },
    skip: options?.skip ?? false,
    ...(options?.revalidate ? { fetchPolicy: 'cache-and-network' as const } : {}),
  });

  const retry = useCallback(() => {
    // The promise is deliberately unawaited: the hook's own state drives the UI,
    // and a failed retry surfaces through `error` like any other failure.
    void refetch();
  }, [refetch]);

  return useMemo(() => {
    const summary = error ? null : (data?.workspaceCreditSummary ?? null);

    return {
      loading,
      failed: error != null,
      retry,
      ...deriveCreditSummaryView(summary, Date.now()),
    };
  }, [data, error, loading, retry]);
}
